import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { useAuthStore } from './store/authStore';
import NotificationPrompt from './components/notifications/NotificationPrompt';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Layouts
import AppLayout from './components/layouts/AppLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Dashboard from './pages/Dashboard';
import Families from './pages/families/Families';
import FamilyDetail from './pages/families/FamilyDetail';
import MemberDetail from './pages/families/MemberDetail';
import JoinFamily from './pages/families/JoinFamily';
import TodoBoards from './pages/todos/TodoBoards';
import TodoBoard from './pages/todos/TodoBoard';
import Documents from './pages/documents/Documents';
import Passwords from './pages/passwords/Passwords';
import ShoppingLists from './pages/shopping/ShoppingLists';
import ShoppingListDetail from './pages/shopping/ShoppingListDetail';
import Calendar from './pages/calendar/Calendar';
import GiftLists from './pages/gifts/GiftLists';
import GiftListDetail from './pages/gifts/GiftListDetail';
import LinkFamily from './pages/families/LinkFamily';
import Settings from './pages/settings/Settings';
import NotFound from './pages/NotFound';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminFamilies from './pages/admin/AdminFamilies';
import AdminData from './pages/admin/AdminData';
import AdminLogs from './pages/admin/AdminLogs';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Component to handle service worker messages
function ServiceWorkerMessageHandler() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const pendingUrlRef = React.useRef<string | null>(null);
  const authStateRef = React.useRef({ isAuthenticated, isLoading });

  // Keep auth state ref updated
  useEffect(() => {
    authStateRef.current = { isAuthenticated, isLoading };
  }, [isAuthenticated, isLoading]);

  // Handle navigation after auth is ready
  useEffect(() => {
    if (!isLoading && isAuthenticated && pendingUrlRef.current) {
      const url = pendingUrlRef.current;
      pendingUrlRef.current = null;
      console.log('[SW Message] Navigating to (after auth):', url);
      // Small delay to ensure route is ready
      setTimeout(() => navigate(url), 100);
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Listen for messages from service worker
    const handleMessage = (event: Event) => {
      const messageEvent = event as MessageEvent;
      if (messageEvent.data && messageEvent.data.type === 'OPEN_URL') {
        const url = messageEvent.data.url;
        if (url) {
          console.log('[SW Message] Received navigation request:', url);
          
          // Use ref to get latest auth state
          const { isLoading: currentLoading, isAuthenticated: currentAuthenticated } = authStateRef.current;
          
          // If auth is still loading, store the URL and ensure auth check runs
          if (currentLoading) {
            pendingUrlRef.current = url;
            checkAuth();
          } else if (currentAuthenticated) {
            // Auth is ready, navigate immediately
            setTimeout(() => navigate(url), 100);
          } else {
            // Not authenticated, redirect to login with return URL
            pendingUrlRef.current = url;
            navigate(`/login?returnUrl=${encodeURIComponent(url)}`);
          }
        }
      }
    };

    // Listen on the service worker controller (current active worker)
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.addEventListener('message', handleMessage);
    }

    // Also listen when a new service worker becomes active
    const handleControllerChange = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.addEventListener('message', handleMessage);
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Wait for service worker to be ready and set up listener
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.active) {
        registration.active.addEventListener('message', handleMessage);
      }
    });

    return () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.removeEventListener('message', handleMessage);
      }
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [navigate]);

  return null;
}

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ServiceWorkerMessageHandler />
        <div className="animated-bg" />
        
        {/* Notification permission prompt */}
        <NotificationPrompt isAuthenticated={isAuthenticated} />
        
        {/* PWA Install prompt */}
        {isAuthenticated && <PWAInstallPrompt />}
        
        <Routes>
          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
          </Route>

          {/* Join family (can be accessed without auth for initial view) */}
          <Route path="/join/:code" element={<JoinFamily />} />

          {/* Protected app routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/families" element={<Families />} />
            <Route path="/families/:id" element={<FamilyDetail />} />
            <Route path="/families/:id/members/:memberId" element={<MemberDetail />} />
            <Route path="/todos" element={<TodoBoards />} />
            <Route path="/todos/:boardId" element={<TodoBoard />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/passwords" element={<Passwords />} />
            <Route path="/shopping" element={<ShoppingLists />} />
            <Route path="/shopping/:listId" element={<ShoppingListDetail />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/gifts" element={<GiftLists />} />
            <Route path="/gifts/:listId" element={<GiftListDetail />} />
            <Route path="/families/link/:inviteCode" element={<LinkFamily />} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="families" element={<AdminFamilies />} />
            <Route path="data" element={<AdminData />} />
            <Route path="logs" element={<AdminLogs />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1e293b',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

